import { Box, Grid, Link } from '@mui/material';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import CardServiceProvider from 'src/components/CardServiceProvider';
import Subtitle from 'src/components/Subtitle';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { findInstitutionByCNPJ } from 'src/services/institution';
import { DebenturesResumeData } from 'src/types/operations/debentures';
import { Media } from 'src/types/strapi';
import { formatCNPJ } from 'src/utils/document';

type OperationDebenturesResumeServiceProviderProps = {
  data?: Pick<
    DebenturesResumeData['serviceProviders'],
    'bancoMandatario' | 'agenteFiduciario' | 'instituicaoDepositaria'
  >;
};

function CardServiceProviderWrap({
  title,
  name,
  logo,
  cnpj,
}: {
  title: string;
  name?: string;
  logo?: Media;
  cnpj?: string;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['institution', cnpj],
    queryFn: () => findInstitutionByCNPJ(formatCNPJ(cnpj as string)),
    enabled: Boolean(cnpj),
  });

  if (!cnpj) {
    return <CardServiceProvider title={title} name={name ?? ''} logo={logo} />;
  }

  if (isLoading || error) return null;

  if (data?.[0]?.slug && data?.[0]?.id) {
    return (
      <Link
        component={RouterLink}
        underline="none"
        to={`/instituicao/${data[0].slug}/${data[0].id}`}>
        <CardServiceProvider
          title={title}
          name={name ?? ''}
          logo={data[0].logo ?? logo}
          link
        />
      </Link>
    );
  }

  return <CardServiceProvider title={title} name={name ?? ''} logo={logo} />;
}

export default function OperationDebenturesResumeServiceProvider({
  data,
}: OperationDebenturesResumeServiceProviderProps) {
  const { auth } = useAuthContext();

  const canViewServiceProviders =
    auth?.user?.subscription?.plan?.permissions
      ?.operacoes_debentures_prestadores_de_servicos;

  return (
    <Box p={1}>
      <Subtitle title="Prestadores de Serviço" />
      <Box py={4}>
        <Grid container spacing={1}>
          {Boolean(data?.bancoMandatario?.length) &&
            data?.bancoMandatario?.map(({ logo, name, cnpj }, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={12}
                lg={6}
                key={cnpj ?? name ?? `banco-mandatario-${index}`}>
                <CardServiceProviderWrap
                  title="Banco Mandatário"
                  logo={logo}
                  name={name ?? ''}
                  cnpj={cnpj}
                />
              </Grid>
            ))}
          {Boolean(data?.agenteFiduciario?.length) &&
            data?.agenteFiduciario?.map(({ logo, name, cnpj }, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={12}
                lg={6}
                key={cnpj ?? name ?? `agente-fiduciario-${index}`}>
                <CardServiceProvider
                  alert={!canViewServiceProviders}
                  lock={!canViewServiceProviders}
                  title="Agente Fiduciário"
                  logo={logo}
                  name={name ?? ''}
                />
              </Grid>
            ))}
          {Boolean(data?.instituicaoDepositaria?.length) &&
            data?.instituicaoDepositaria?.map(({ logo, name, cnpj }, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={12}
                lg={6}
                key={cnpj ?? name ?? `instituicao-depositaria-${index}`}>
                <CardServiceProvider
                  alert={!canViewServiceProviders}
                  lock={!canViewServiceProviders}
                  title="Instituição Depositária"
                  logo={logo}
                  name={name ?? ''}
                />
              </Grid>
            ))}
        </Grid>
      </Box>
    </Box>
  );
}
